"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Mail, ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function FinalCta() {
  const shouldReduceMotion = useReducedMotion();
  const nearbyAreas = [{
    name: "Guildford",
    to: "/AreasWeCoverPage"
  }, {
    name: "Woking",
    to: "/AreasWeCoverPage"
  }];
  return <section id="finalcta" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative">
          {/* Main Card */}
          <Card className="relative overflow-hidden border border-border bg-card shadow-lg">
            <CardContent className="p-8 sm:p-10 lg:p-14">
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-12">
                {/* Left Column: Heading & Information */}
                <motion.div initial={shouldReduceMotion ? false : {
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5
              }} className="lg:col-span-7">
                  <div className="mb-4 inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-xs font-semibold text-secondary">
                    <ShieldCheck className="size-4 text-primary" />
                    <span>NASC &amp; CHAS Accredited</span>
                  </div>

                  <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                    Request a scaffolding quote
                  </h2>

                  <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
                    Speak directly with our contracts team for commercial, residential, roofing, or specialist access requirements across Surrey, Berkshire, and the South East.
                  </p>

                  <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                    <Button asChild size="lg" className="w-full sm:w-auto">
                      <Link to="/ContactRequestQuotePage" className="flex items-center gap-2">
                        <span>Get a quote online</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                      <Link to="/ProjectsCaseStudiesPage">View our past projects</Link>
                    </Button>
                  </div>
                </motion.div>

                {/* Right Column: Direct Contact Details Box */}
                <motion.div initial={shouldReduceMotion ? false : {
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: 0.1
              }} className="lg:col-span-5">
                  <div className="rounded-xl border border-border bg-muted/40 p-6 sm:p-8">
                    <h3 className="text-lg font-semibold text-foreground">
                      Direct contact
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Reach our operations desk directly during working hours.
                    </p>

                    <div className="mt-6 space-y-4">
                      {/* Phone link */}
                      <a href="tel:01344929900" className="group flex items-center gap-4 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-2xs transition-[border-color,box-shadow,color] duration-200 hover:border-primary/50 hover:shadow-xs focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <div className="flex size-11 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
                          <Phone className="size-5" />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-muted-foreground group-hover:text-foreground">
                            Call our team
                          </div>
                          <div className="text-base font-semibold text-foreground transition-colors group-hover:text-primary group-focus-visible:text-primary">
                            01344 929900
                          </div>
                        </div>
                      </a>

                      {/* Email link */}
                      <a href="mailto:quotes@bmfscaffolding.co.uk" className="group flex items-center gap-4 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-2xs transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-xs focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <div className="flex size-11 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                          <Mail className="size-5" />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-muted-foreground group-hover:text-foreground">
                            Email specifications &amp; drawings
                          </div>
                          <div className="text-sm font-semibold text-foreground transition-colors group-hover:text-primary group-focus-visible:text-primary sm:text-base">
                            quotes@bmfscaffolding.co.uk
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Nearby Areas section */}
              <div className="mt-10 pt-8">
                <Separator className="mb-6" />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <MapPin className="size-4 text-primary" />
                    <span>Nearby areas covered:</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    {nearbyAreas.map((area, index) => <div key={area.name} data-index={index} className="inline-flex items-center gap-4">
                        <Link to={area.to} className="font-medium text-muted-foreground transition-colors hover:text-primary hover:underline">
                          {area.name}
                        </Link>
                        {index < nearbyAreas.length - 1 && <span className="text-muted-foreground/40">•</span>}
                      </div>)}
                    <span className="text-muted-foreground/40">•</span>
                    <Link to="/AreasWeCoverPage" className="text-xs font-semibold uppercase tracking-wider text-primary hover:underline">
                      View all coverage areas
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}